// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/readiness/ReadinessCheckList.jsx

import React, { useMemo, useState } from "react";

function titleize(value) {
  return String(value || "")
    .replace(/[_.-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const GROUP_LABELS = {
  identity: "Identity & tenant context",
  organization: "Organization setup",
  users: "Users & administrators",
  roles: "Roles & permissions",
  connectors: "Connector governance",
  audit: "Audit traceability",
  guardrails: "Critical guardrails",
};

const GROUP_DESCRIPTIONS = {
  identity: "Auth0 verification and tenant-scoped request context.",
  organization: "Organization profile and operating defaults.",
  users: "Privileged administrators and user governance.",
  roles: "Role catalog, permission catalog, and request permissions.",
  connectors: "Connector registry, ownership, and attention gaps.",
  audit: "Governance ledger and export readiness.",
  guardrails: "Read-only readiness checks and non-destructive guarantees.",
};

function groupChecks(checks) {
  return checks.reduce((groups, check) => {
    const key = typeof check.category === "string" ? check.category : "connectors";
    groups[key] = groups[key] || [];
    groups[key].push(check);
    return groups;
  }, {});
}

function statusLabel(status) {
  if (status === "passed") return "Passed";
  if (status === "warning") return "Warning";
  if (status === "blocked") return "Blocked";
  return titleize(status || "Unknown");
}

function groupStatus(items) {
  const blocked = items.filter((item) => item.status === "blocked").length;
  const warnings = items.filter((item) => item.status === "warning").length;

  if (blocked) return { label: `${blocked} blocked`, className: "has-blocked", hasIssue: true };
  if (warnings) return { label: `${warnings} warning`, className: "has-warning", hasIssue: true };
  return { label: "All clear", className: "is-clear", hasIssue: false };
}

export default function ReadinessCheckList({ checks = [] }) {
  const [openGroups, setOpenGroups] = useState({});

  const grouped = useMemo(() => groupChecks(checks), [checks]);

  if (!checks.length) {
    return (
      <div className="enterprise-admin-empty compact">
        <strong>No readiness checks loaded</strong>
        <p>Refresh readiness to evaluate organization readiness.</p>
      </div>
    );
  }

  return (
    <div className="enterprise-admin-readiness-checks">
      {Object.entries(grouped).map(([group, items]) => {
        const status = groupStatus(items);
        const isOpen = openGroups[group] ?? status.hasIssue;

        return (
          <section
            key={group}
            className={`enterprise-admin-readiness-group ${status.hasIssue ? "has-issue" : "is-quiet"}`}
          >
            <button
              type="button"
              className="enterprise-admin-readiness-group-header"
              onClick={() => setOpenGroups((current) => ({ ...current, [group]: !isOpen }))}
              aria-expanded={isOpen}
            >
              <span className="enterprise-admin-readiness-group-title">
                <strong>{GROUP_LABELS[group] || titleize(group)}</strong>
                <span>{GROUP_DESCRIPTIONS[group] || "Tenant readiness checks."}</span>
              </span>
              <span className={`enterprise-admin-readiness-group-status ${status.className}`}>
                {status.label}
              </span>
              <span
                className={`enterprise-admin-readiness-chevron ${isOpen ? "is-open" : "is-closed"}`}
                aria-hidden="true"
              />
            </button>

            {isOpen ? (
              <div className="enterprise-admin-readiness-check-list">
                {items.map((check) => (
                  <article key={check.key} className={`enterprise-admin-readiness-check status-${check.status}`}>
                    <span className="enterprise-admin-readiness-status-dot" aria-hidden="true" />
                    <div className="enterprise-admin-readiness-check-body">
                      <strong>{check.label}</strong>
                      <p>{check.detail}</p>
                      {check.remediation && check.status !== "passed" ? (
                        <small>{check.remediation}</small>
                      ) : null}
                    </div>
                    <span className="enterprise-admin-readiness-check-badge">
                      {statusLabel(check.status)}
                    </span>
                  </article>
                ))}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
